import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import FindoConfirmDialog from "@/components/FindoConfirmDialog";
import Logo from "@/components/Logo";
import { FileClock, Trash2, X } from "lucide-react";

const DISMISS_KEY = "findo_draft_banner_dismissed";

export default function DraftResumeBanner() {
  const { user } = useB2C() || {};
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [confirmDel, setConfirmDel] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) || "");

  useEffect(() => {
    if (!user) { setDrafts([]); return; }
    api("/b2c/drafts", { b2c: true }).then((d) => setDrafts(Array.isArray(d) ? d : [])).catch(() => {});
  }, [user, location.pathname]);

  const d = drafts[0];
  const onDraftPage = d && location.pathname === d.path.split("?")[0];
  if (!d || onDraftPage || dismissed === d.key + d.updated_at) return null;
  const hoursLeft = Math.max(1, Math.round((new Date(d.expires_at) - Date.now()) / 3600000));

  async function remove() {
    setConfirmDel(false);
    try {
      await api(`/b2c/drafts?key=${encodeURIComponent(d.key)}`, { method: "DELETE", b2c: true });
      setDrafts((x) => x.slice(1));
      toast.success(t("dr.deleted"));
    } catch (e) { toast.error(e.message); }
  }
  const dismiss = () => { const v = d.key + d.updated_at; sessionStorage.setItem(DISMISS_KEY, v); setDismissed(v); };

  return (
    <div className="fixed bottom-5 left-5 z-40 w-[calc(100%-2.5rem)] max-w-md rounded-2xl bg-findo-navy text-white shadow-2xl p-4 animate-in slide-in-from-bottom-4" data-testid="draft-resume-banner">
      <button onClick={dismiss} className="absolute top-3 right-3 text-white/60 hover:text-white" aria-label="close" data-testid="draft-banner-close"><X size={16} /></button>
      <div className="flex items-start gap-3 pr-5">
        <Logo size={34} />
        <div className="min-w-0">
          <p className="font-semibold flex items-center gap-1.5"><FileClock size={15} className="text-findo-red" /> {t("dr.title")}</p>
          <p className="text-xs text-white/70 mt-1">
            {d.label ? `« ${d.label} » · ` : ""}{t("dr.expiresIn").replace("{h}", hoursLeft)}
          </p>
        </div>
      </div>
      <div className="mt-3.5 flex gap-2">
        <Button size="sm" onClick={() => navigate(d.path)} className="flex-1 rounded-full font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="draft-continue-btn">{t("dr.continue")}</Button>
        <Button size="sm" variant="outline" onClick={() => setConfirmDel(true)} className="flex-1 rounded-full font-semibold bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="draft-delete-btn">
          <Trash2 size={14} className="mr-1.5" /> {t("dr.delete")}
        </Button>
      </div>
      <FindoConfirmDialog open={confirmDel} text={t("dr.deleteConfirm")} okLabel={t("dr.delete")} danger testid="draft-delete"
        onClose={() => setConfirmDel(false)} onConfirm={remove} />
    </div>
  );
}
